import { ExternalChatMessage } from "domain-entities";
import { isImageMimeType } from "Helpers/AssetHelper";
import { useApp } from "Providers/AppProvider";
import { useEffect, useState } from "react";
import { useSnackbar } from "material-ui-snackbar-provider";

export interface MessageMedia {
  isLoading: boolean;
  url?: string;
  error?: Error;
}

export function useDownloadMessageMedia() {
  const { firebaseStorage } = useApp();
  const snackbar = useSnackbar();

  return async (message?: ExternalChatMessage) => {
    if (firebaseStorage && message?.media) {
      const url = await firebaseStorage
        .ref(message.media.location)
        .getDownloadURL();

      window.open(url, "_blank")?.focus();
    } else {
      snackbar.showMessage("Can not download media.");
    }
  };
}

export function useMessageMedia(
  message: ExternalChatMessage,
  minThumbnailSize?: number
): MessageMedia {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const [mediaUrl, setMediaUrl] = useState<string>();
  const { firebaseStorage } = useApp();

  useEffect(() => {
    let isMounted = true;

    async function startEffect() {
      setIsLoading(true);
      if (message.media && firebaseStorage) {
        try {
          const thumbnail = minThumbnailSize
            ? message.media.thumbnails
                .sort((a, b) => a.width - b.width)
                .find((thumbnail) => thumbnail.width >= minThumbnailSize)
            : undefined;

          const downloadUrl = await firebaseStorage
            .ref(thumbnail?.location || message.media.location)
            .getDownloadURL();

          if (typeof downloadUrl === "string") {
            if (isImageMimeType(message.media.mimeType)) {
              const image = new Image();

              image.onload = () => {
                isMounted && setMediaUrl(downloadUrl);
                isMounted && setIsLoading(false);
              };

              image.src = downloadUrl;
            } else {
              isMounted && setMediaUrl(downloadUrl);
              isMounted && setIsLoading(false);
            }
          } else {
            isMounted && setError(new Error("Download URL is not a string."));
            isMounted && setIsLoading(false);
          }
        } catch (error) {
          isMounted && setError(error);
          isMounted && setIsLoading(false);
        }
      }
    }

    startEffect();

    return () => {
      isMounted = false;
    };
  }, [message.media, firebaseStorage, minThumbnailSize]);

  return {
    url: mediaUrl,
    isLoading,
    error,
  };
}
